import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Accordion, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { getLenderMatchSettings, toggleLenderMatch, getAvailableFields, updateLenderCriteria, deleteCriterion } from '../api/lenderApi';
import '../styles/FieldMappings.css'; // Reuse the same styles as FieldMappings

/**
 * LenderMatch component for configuring lender recommendation criteria
 * 
 * This component allows administrators to:
 * - Enable/disable the lender recommendation system
 * - Configure criteria for each lender
 * - Set weights and required flags for criteria
 * 
 * @returns {JSX.Element} Lender match settings component
 */
const LenderMatch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success, error: notifyError } = useNotifications();
  
  // State variables
  const [settings, setSettings] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lenders, setLenders] = useState([]);
  const [shouldReload, setShouldReload] = useState(false);
  const [criteriaChanged, setCriteriaChanged] = useState({}); // Track changes to criteria for each lender
  
  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch lender match settings
        const settingsData = await getLenderMatchSettings();
        setSettings(settingsData);
        setEnabled(settingsData.enabled);
        
        // Fetch available fields for criteria
        const fields = await getAvailableFields();
        setAvailableFields(fields);
        
        // Get available lenders from the LenderSubmission component
        // This would be a hard-coded list that matches what's available in the app
        const availableLenders = [
          // Platform API Lenders
          { id: 'kapitus', name: 'Kapitus' },
          { id: 'credibly', name: 'Credibly' },
          { id: 'ondeck', name: 'OnDeck Capital' },
          { id: 'forward', name: 'Forward Financing' },
          { id: 'rapid', name: 'Rapid Finance' },
          { id: 'pirs', name: 'PIRS Capital' },
          { id: 'channel', name: 'Channel Partners' },
          { id: 'headway', name: 'Headway Capital' },
          
          // Email Submission Lenders
          { id: 'backd', name: 'Backd' },
          { id: 'bmt', name: 'BMT' },
          { id: 'cashfloit', name: 'CashFlo IT' },
          { id: 'cfg', name: 'CFG' },
          { id: 'ebf', name: 'EBF' },
          { id: 'ecg', name: 'ECG' },
          { id: 'fox', name: 'Fox' },
          { id: 'fresh', name: 'Fresh' },
          { id: 'front', name: 'Front' },
          { id: 'fundr', name: 'Fundr' },
          { id: 'gfe', name: 'GFE' },
          { id: 'iou', name: 'IOU' },
          { id: 'legend', name: 'Legend' },
          { id: 'legendca', name: 'Legend CA' },
          { id: 'newco', name: 'NewCo' },
          { id: 'nexi', name: 'Nexi' },
          { id: 'snapadvances', name: 'Snap Advances' },
          { id: 'spartan', name: 'Spartan' },
          { id: 'surfside', name: 'Surfside' },
          { id: 'trofeo', name: 'Trofeo' },
          { id: 'ufs', name: 'UFS' },
          { id: 'velocitycapital', name: 'Velocity Capital' }
        ];
        
        // Merge available lenders with configured lenders
        const mergedLenders = availableLenders.map(lender => {
          const configuredLender = settingsData.lenders.find(l => l.lenderId === lender.id);
          return {
            ...lender,
            criteria: configuredLender ? configuredLender.criteria : []
          };
        });
        
        setLenders(mergedLenders);
        
        // Reset criteria changed tracking after reload
        setCriteriaChanged({});
        
        // Reset the reload flag
        if (shouldReload) {
          setShouldReload(false);
        }
      } catch (err) {
        console.error('Error fetching lender match settings:', err);
        setError('Failed to load lender match settings. Please try again later.');
        notifyError('Failed to load lender match settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [shouldReload]); // Only depend on shouldReload flag
  
  // Log when criteria change to help debug
  useEffect(() => {
    lenders.forEach(lender => {
      const dateAgeCriteria = lender.criteria.filter(c => 
        isDateComparisonField(c.fieldId) && c.operator === 'older_than_months'
      );
      
      if (dateAgeCriteria.length > 0) {
        console.log('Date age criteria detected:', dateAgeCriteria);
      }
    });
  }, [lenders]);
  
  // Handle toggle change
  const handleToggleChange = async (e) => {
    const newEnabled = e.target.checked;
    
    try {
      setSaving(true);
      
      // Update the enabled status
      await toggleLenderMatch(newEnabled);
      
      // Update local state without a full refresh
      setEnabled(newEnabled);
      
      // Show success notification
      success(`Lender recommendations ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error toggling lender match:', err);
      setError('Failed to update lender match status.');
      notifyError('Failed to update lender match status');
      
      // Only reload on error
      setShouldReload(true);
    } finally {
      setSaving(false);
    }
  };
  
  // Add a new criterion to a lender
  const addCriterion = (lenderId) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    
    if (lenderIndex === -1) return;
    
    // Create a new criterion with default values
    const newCriterion = {
      fieldId: '',
      fieldName: '',
      operator: '>=',
      value: '',
      weight: 5,
      required: false
    };
    
    // Add the criterion to the lender
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].criteria.push(newCriterion);
    
    // Update state
    setLenders(updatedLenders);
    
    // Mark criteria as changed for this lender
    setCriteriaChanged(prev => ({
      ...prev,
      [lenderId]: true
    }));
  };
  
  // Update a criterion field
  const updateCriterion = (lenderId, criterionIndex, field, value) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    
    if (lenderIndex === -1) return;
    
    // Create updated lenders array
    const updatedLenders = [...lenders];
    
    // If changing the field ID, also update the field name
    if (field === 'fieldId' && value) {
      const selectedField = availableFields.find(f => f.id === value);
      
      if (selectedField) {
        updatedLenders[lenderIndex].criteria[criterionIndex] = {
          ...updatedLenders[lenderIndex].criteria[criterionIndex],
          fieldId: value,
          fieldName: selectedField.name
        };
      }
    } else if (field === 'operator') {
      // When changing operator, adjust the value as needed
      const criterion = updatedLenders[lenderIndex].criteria[criterionIndex];
      const fieldId = criterion.fieldId;
      
      // Set appropriate default values based on new operator
      let newValue = criterion.value;
      
      if (value === 'older_than_months') {
        // Default to 12 months for business start date and 252 months (21 years) for DOB
        newValue = fieldId === 'BusinessStartDate' ? '12' : fieldId === 'DateOfBirth' ? '252' : '12';
      } else if (isDateComparisonField(fieldId) && criterion.operator === 'older_than_months') {
        // If switching from older_than_months to something else for a date field, reset value
        newValue = '';
      }
      
      updatedLenders[lenderIndex].criteria[criterionIndex] = {
        ...criterion,
        operator: value,
        value: newValue
      };
    } else {
      // Otherwise just update the specified field
      updatedLenders[lenderIndex].criteria[criterionIndex] = {
        ...updatedLenders[lenderIndex].criteria[criterionIndex],
        [field]: value
      };
    }
    
    // Update state
    setLenders(updatedLenders);
    
    // Mark criteria as changed for this lender
    setCriteriaChanged(prev => ({
      ...prev,
      [lenderId]: true
    }));
  };
  
  // Remove a criterion
  const removeCriterion = (lenderId, criterionIndex) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    
    if (lenderIndex === -1) return;
    
    // Create updated lenders array
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].criteria.splice(criterionIndex, 1);
    
    // Update state
    setLenders(updatedLenders);
    
    // Mark criteria as changed for this lender
    setCriteriaChanged(prev => ({
      ...prev,
      [lenderId]: true
    }));
  };
  
  // Save changes for a specific lender
  const saveLenderCriteria = async (lenderId) => {
    const lender = lenders.find(l => l.id === lenderId);
    
    if (!lender) return;
    
    try {
      setSaving(true);
      
      // Filter out any criteria with empty fieldId
      const validCriteria = lender.criteria.filter(c => c.fieldId);
      
      // Update the criteria in the backend
      await updateLenderCriteria(lender.id, lender.name, validCriteria);
      
      // Reset change tracking for this lender
      setCriteriaChanged(prev => ({
        ...prev,
        [lenderId]: false
      }));
      
      // Trigger a single reload of data
      setShouldReload(true);
      
      // Show success notification
      success(`Criteria updated for ${lender.name}`);
    } catch (err) {
      console.error('Error saving lender criteria:', err);
      setError(`Failed to save criteria for ${lender.name}.`);
      notifyError(`Failed to save criteria for ${lender.name}`);
    } finally {
      setSaving(false);
    }
  };
  
  // List of field IDs that contain numeric values but are stored as text
  const numericTextFields = [
    'AverageBankBalance',
    'AnnualRevenue',
    'RequestedAmount',
    'ContactOwnershipPercentage',
    'FederalTaxID',
    // Add any other fields that should be treated as numbers
  ];
  
  // Helper function to determine if a field should use numeric operators
  const shouldUseNumericOperators = (fieldId, type) => {
    // If it's already a number type, return true
    if (type === 'number') return true;
    
    // If it's in our list of numeric text fields, return true
    return numericTextFields.includes(fieldId);
  };
  
  // Helper function to check if it's a field that should have the "Older than" operator
  const isDateComparisonField = (fieldId) => {
    return fieldId === 'BusinessStartDate' || fieldId === 'DateOfBirth';
  };
  
  // Get field type options based on field type
  const getFieldOptions = (fieldId) => {
    const field = availableFields.find(f => f.id === fieldId);
    
    if (!field) return [];
    
    const type = field.type.toLowerCase();
    
    // Special case for BusinessStartDate and DateOfBirth
    if (isDateComparisonField(fieldId)) {
      return [
        { value: '>', label: 'After' },
        { value: '>=', label: 'On or after' },
        { value: '=', label: 'On' },
        { value: '<=', label: 'On or before' },
        { value: '<', label: 'Before' },
        { value: 'older_than_months', label: 'Older than (months)' }
      ];
    } else if (shouldUseNumericOperators(fieldId, type)) {
      return [
        { value: '>', label: 'Greater than' },
        { value: '>=', label: 'Greater than or equal to' },
        { value: '=', label: 'Equals' },
        { value: '<=', label: 'Less than or equal to' },
        { value: '<', label: 'Less than' }
      ];
    } else if (type === 'string') {
      return [
        { value: 'contains', label: 'Contains' },
        { value: '=', label: 'Equals' }
      ];
    } else if (type === 'date') {
      return [
        { value: '>', label: 'After' },
        { value: '>=', label: 'On or after' },
        { value: '=', label: 'On' },
        { value: '<=', label: 'On or before' },
        { value: '<', label: 'Before' },
        { value: 'older_than_months', label: 'Older than (months)' }
      ];
    } else if (type === 'boolean') {
      return [
        { value: '=', label: 'Equals' }
      ];
    }
    
    return [
      { value: '=', label: 'Equals' }
    ];
  };
  
  // Get appropriately typed input for the field
  const getFieldInput = (fieldId, value, onChange, operator) => {
    const field = availableFields.find(f => f.id === fieldId);
    
    if (!field) return <Form.Control type="text" value={value} onChange={onChange} placeholder="Enter value" />;
    
    const type = field.type.toLowerCase();
    
    // Special input for "Older than" operator for date fields
    if (isDateComparisonField(fieldId) && operator === 'older_than_months') {
      // Create options for 1-36 months
      const monthOptions = Array.from({ length: 36 }, (_, i) => i + 1);
      
      return (
        <Form.Select 
          value={value} 
          onChange={onChange}
          placeholder="Select months"
        >
          <option value="">Select months</option>
          {monthOptions.map(months => (
            <option key={months} value={months}>
              {months} {months === 1 ? 'month' : 'months'}
            </option>
          ))}
        </Form.Select>
      );
    } else if (shouldUseNumericOperators(fieldId, type)) {
      return (
        <Form.Control 
          type="number" 
          value={value} 
          onChange={onChange} 
          placeholder="Enter numeric value" 
        />
      );
    } else if (type === 'string') {
      return (
        <Form.Control 
          type="text" 
          value={value} 
          onChange={onChange} 
          placeholder="Enter text value" 
        />
      );
    } else if (type === 'date') {
      return (
        <Form.Control 
          type="date" 
          value={value} 
          onChange={onChange} 
        />
      );
    } else if (type === 'boolean') {
      return (
        <Form.Select value={value} onChange={onChange}>
          <option value="true">True</option>
          <option value="false">False</option>
        </Form.Select>
      );
    }
    
    return <Form.Control type="text" value={value} onChange={onChange} placeholder="Enter value" />;
  };
  
  // Group fields by category for better organization
  const fieldsByCategory = availableFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {});
  
  // Create field options for Select component
  const fieldOptions = Object.entries(fieldsByCategory).map(([category, fields]) => ({
    label: category,
    options: fields.map(field => ({
      value: field.id,
      label: field.name,
      type: field.type
    }))
  }));
  
  // Add this at the top level with other constant definitions
  const selectStyles = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999
    })
  };
  
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading lender match settings...</p>
      </Container>
    );
  }
  
  return (
    <Container className="my-5">
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center mb-4">
            <Col>
              <h2 className="mb-0">Lender Match Settings</h2>
              <p className="text-muted mt-1">
                Configure criteria to recommend the best lenders for each contact
              </p>
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="lender-match-toggle"
                label={enabled ? "Enabled" : "Disabled"}
                checked={enabled}
                onChange={handleToggleChange}
                disabled={saving}
                className="fs-5 lender-match-toggle"
              />
            </Col>
          </Row>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          <Alert variant="info" className="mb-4">
            <Alert.Heading>How Lender Matching Works</Alert.Heading>
            <p>
              For each lender, you can define criteria that will be evaluated against contact data.
              Each criterion has a weight (1-10) that determines its importance in the final score.
              Required criteria must be met for the lender to be considered at all.
            </p>
            <p className="mb-0">
              When enabled, match scores will be displayed in the lender submission section
              to help users choose the most appropriate lender for each contact.
            </p>
            <hr />
            <p>
              <strong>Special Features:</strong>
            </p>
            <ul className="mb-0">
              <li>
                <strong>Numeric Text Fields:</strong> Some text fields containing numeric values (like "Average Ending Balance") 
                can be compared using numeric operators (&gt;, &lt;, etc.) for more flexible matching criteria.
              </li>
              <li>
                <strong>Date Age Comparison:</strong> For BusinessStartDate and DateOfBirth fields, you can use the "Older than (months)" 
                operator to match based on how many months have passed since the date. For example:
                <ul>
                  <li>To match businesses that have been operating for at least 12 months, set BusinessStartDate "Older than (months)" to 12.</li>
                  <li>To match borrowers over 21 years old, set DateOfBirth "Older than (months)" to 252 (21 years × 12 months).</li>
                  <li>The system calculates months by comparing the current date to the date field value.</li>
                </ul>
              </li>
            </ul>
          </Alert>
          
          <Accordion className="mb-4">
            {lenders.map((lender, index) => (
              <Accordion.Item key={lender.id} eventKey={lender.id}>
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 pe-4">
                    <span>{lender.name}</span>
                    <Badge bg="secondary">
                      {lender.criteria.length} {lender.criteria.length === 1 ? 'criterion' : 'criteria'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {lender.criteria.length === 0 ? (
                    <Alert variant="light" className="text-center mb-3">
                      No criteria defined yet. Add criteria to improve lender matching.
                    </Alert>
                  ) : (
                    <div className="mb-4">
                      {lender.criteria.map((criterion, criterionIndex) => (
                        <Card key={criterionIndex} className="mb-3 border criterion-card">
                          <Card.Body style={{ paddingBottom: '1.5rem' }}>
                            <Row className="mb-3">
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label>Field</Form.Label>
                                  <Select
                                    options={fieldOptions}
                                    value={fieldOptions.flatMap(group => group.options).find(opt => opt.value === criterion.fieldId)}
                                    onChange={(selected) => updateCriterion(lender.id, criterionIndex, 'fieldId', selected.value)}
                                    placeholder="Select a field"
                                    isDisabled={saving}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    className="select-field"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>Operator</Form.Label>
                                  <Select
                                    options={getFieldOptions(criterion.fieldId)}
                                    value={criterion.fieldId ? 
                                      getFieldOptions(criterion.fieldId).find(opt => opt.value === criterion.operator) || null : null}
                                    onChange={(selected) => updateCriterion(lender.id, criterionIndex, 'operator', selected.value)}
                                    placeholder="Select operator"
                                    isDisabled={!criterion.fieldId || saving}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    className="select-operator"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={5}>
                                <Form.Group>
                                  <Form.Label>Value</Form.Label>
                                  {getFieldInput(
                                    criterion.fieldId,
                                    criterion.value,
                                    (e) => updateCriterion(lender.id, criterionIndex, 'value', e.target.value),
                                    criterion.operator
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Weight (1-10)</Form.Label>
                                  <Form.Range
                                    min={1}
                                    max={10}
                                    value={criterion.weight}
                                    onChange={(e) => updateCriterion(lender.id, criterionIndex, 'weight', Number(e.target.value))}
                                    disabled={saving}
                                  />
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Less important</span>
                                    <span className="fw-bold">{criterion.weight}</span>
                                    <span className="text-muted">More important</span>
                                  </div>
                                </Form.Group>
                              </Col>
                              <Col md={3} className="d-flex align-items-center">
                                <Form.Check
                                  type="checkbox"
                                  id={`required-${lender.id}-${criterionIndex}`}
                                  label="Required"
                                  checked={criterion.required}
                                  onChange={(e) => updateCriterion(lender.id, criterionIndex, 'required', e.target.checked)}
                                  disabled={saving}
                                />
                              </Col>
                              <Col md={3} className="d-flex align-items-center justify-content-end">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeCriterion(lender.id, criterionIndex)}
                                  disabled={saving}
                                >
                                  Remove
                                </Button>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between">
                    <Button
                      variant="outline-primary"
                      onClick={() => addCriterion(lender.id)}
                      disabled={saving}
                    >
                      Add Criterion
                    </Button>
                    
                    <Button
                      variant="primary"
                      onClick={() => saveLenderCriteria(lender.id)}
                      disabled={saving || !criteriaChanged[lender.id]}
                    >
                      {saving ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : 'Save Criteria'}
                    </Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LenderMatch; 